import {
  LogGAClickEventParams,
  getOrCreateVisitorId,
  logGAEvent,
  logPageViewEvent,
  subscribeAnalyticsConsent,
  setAnalyticsUserProperties,
  TrackGAServiceEventParams
} from 'lib/firebase/ga_analytics';
import GA_EVENTS from 'lib/firebase/ga_events';
import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';
import { useLocation } from 'react-router-dom';
import { LooseObject } from 'services/interface';
import { useFirebaseAuth } from './useFirebaseAuth';
import useIsConsented from './useIsConsented';

interface ViewImpressionParams extends LooseObject {
  view_event_name: string;
}

interface PreviousAndCurrentRouteInterface {
  previousRoute: string;
  currentRoute: string;
  currentRouteWithSearchQuery: string;
  previousRouteWithSearchQuery: string;
}

interface Context {
  trackGAEvent: typeof logGAEvent;
  trackGAClickEvent: (eventDetails: LogGAClickEventParams) => void;
  trackGAServiceEvent: (eventDetails: TrackGAServiceEventParams) => void;
  trackGAExperimentActivated: (eventDetails: {
    experiment_id: string;
    variation_id: string;
  }) => void;
  viewImpression: (eventDetails: ViewImpressionParams) => void;
  previousAndCurrentRoute: PreviousAndCurrentRouteInterface;
  isIgnorePageViewEvent: boolean;
  setIsIgnorePageViewEvent: (value: boolean) => void;
}

const AnalyticsContext = createContext<Context | null>(null);

const ignoreRoutes = ['/retreats', '/retreat', '/search', '/sleep'];
const IGNORED_TITLES = [
  'Insight Timer',
  'Insight Timer - #1 Free Meditation App for Sleep, Relax & More'
];

export const AnalyticsProvider: React.FC<PropsWithChildren> = ({
  children
}) => {
  const [isIgnorePageViewEvent, setIsIgnorePageViewEvent] = useState(false);
  const location = useLocation();
  const isConsented = useIsConsented();

  const [previousAndCurrentRoute, setPreviousAnsCurrentRoute] =
    useState<PreviousAndCurrentRouteInterface>({
      previousRoute: location.pathname,
      currentRoute: location.pathname,
      previousRouteWithSearchQuery: `${location.pathname}${
        location.search ?? ''
      }`,
      currentRouteWithSearchQuery: `${location.pathname}${
        location.search ?? ''
      }`
    });

  const { user, isSubscriber } = useFirebaseAuth();

  const [documentTitle, setDocumentTitle] = useState<string>(document.title); // Store document title

  useEffect(() => {
    const visitorId = getOrCreateVisitorId();

    const applyUserProperties = () => {
      setAnalyticsUserProperties({
        userId: user?.uid,
        isSubscriber,
        visitorId
      });
    };

    applyUserProperties();

    const unsubscribe = subscribeAnalyticsConsent(consented => {
      if (consented) {
        applyUserProperties();
      }
    });

    return unsubscribe;
  }, [isSubscriber, user]);

  useEffect(() => {
    const titleElement = document.querySelector('title');

    if (!titleElement) {
      return undefined;
    }

    const documentTitleObserver = new MutationObserver(() => {
      setDocumentTitle(document.title);
    });

    // observe changes happen in title tag
    documentTitleObserver.observe(titleElement, {
      childList: true
    });

    return () => {
      documentTitleObserver.disconnect();
    };
  }, [location.pathname, previousAndCurrentRoute]);

  useEffect(() => {
    setPreviousAnsCurrentRoute(details => {
      const newRouteWithSearch = `${location.pathname}${location.search ?? ''}`;

      if (newRouteWithSearch === details.currentRouteWithSearchQuery) {
        return details;
      }

      return {
        previousRoute: details.currentRoute,
        currentRoute: location.pathname,
        previousRouteWithSearchQuery: details.currentRouteWithSearchQuery,
        currentRouteWithSearchQuery: newRouteWithSearch
      };
    });
  }, [location]);

  useEffect(() => {
    const { previousRoute, currentRoute } = previousAndCurrentRoute;

    if (IGNORED_TITLES.includes(documentTitle)) {
      return;
    }

    if (
      isConsented &&
      !isIgnorePageViewEvent &&
      !ignoreRoutes.some(route => currentRoute.startsWith(route))
    ) {
      logPageViewEvent({
        page_name: currentRoute,
        previous_page_name: previousRoute,
        user_agent: navigator?.userAgent,
        page_title: documentTitle
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentTitle, isConsented]);

  const trackGAClickEvent = useCallback(
    (eventDetails: LogGAClickEventParams): void => {
      logGAEvent({
        event_name: 'click',
        page_name: previousAndCurrentRoute.currentRoute,
        previous_page_name: previousAndCurrentRoute.previousRoute,
        user_agent: navigator?.userAgent,
        ...eventDetails
      });
    },
    [previousAndCurrentRoute]
  );

  const trackGAServiceEvent = useCallback(
    (eventDetails: TrackGAServiceEventParams): void => {
      logGAEvent({
        event_name: GA_EVENTS.SERVICE_EVENT,
        page_name: previousAndCurrentRoute.currentRoute,
        previous_page_name: previousAndCurrentRoute.previousRoute,
        user_agent: navigator?.userAgent,
        ...eventDetails
      });
    },
    [previousAndCurrentRoute]
  );

  const viewImpression = useCallback(
    (eventDetails: ViewImpressionParams) => {
      logGAEvent({
        event_name: GA_EVENTS.VIEW,
        page_name: previousAndCurrentRoute.currentRoute,
        previous_page_name: previousAndCurrentRoute.previousRoute,
        ...eventDetails
      });
    },
    [previousAndCurrentRoute]
  );

  const trackGAExperimentActivated = useCallback(
    (eventDetails: { experiment_id: string; variation_id: string }): void => {
      logGAEvent({
        event_name: GA_EVENTS.EXPERIMENT_ACTIVATED,
        page_name: previousAndCurrentRoute.currentRoute,
        previous_page_name: previousAndCurrentRoute.previousRoute,
        user_agent: navigator?.userAgent,
        ...eventDetails
      });
    },
    [previousAndCurrentRoute]
  );

  return (
    <AnalyticsContext.Provider
      value={{
        trackGAEvent: logGAEvent,
        trackGAClickEvent,
        trackGAServiceEvent,
        trackGAExperimentActivated,
        viewImpression,
        previousAndCurrentRoute,
        isIgnorePageViewEvent,
        setIsIgnorePageViewEvent
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);

  if (!context) {
    throw new Error('useAnalytics must be use inside AnalyticsProvider');
  }
  return context;
};
